import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import * as Yup from 'yup';
import { useLocker } from '../context/useLocker';
import { useToast } from '../../../shared/hooks/useToast';
import { useAuth } from '../../auth/context/AuthContext';
import { equipmentService } from '../../../shared/services/equipments/equipmentsServices';
import { manufacturerService } from '../../../shared/services/manufacturers/manufacturersServices';
import { roomService } from '../../../shared/services/rooms/roomsServices';

interface LockerFormValues {
  name: string;
  equipmentId: string;
  manufacturerId: string;
  roomId: string;
  is_available: boolean;
  locker_quantity: number;
}

interface LockerPayload {
  name: string;
  tenantId: string;
  equipmentId: string;
  manufacturerId: string;
  roomId: string;
  is_available: boolean;
  number: string;
  locker_quantity?: number;
}

export function useLockerForm(id?: string) {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const auth = useAuth();
  const { 
    lockerData,
    isDataLoading,
    createLocker,
    updateLocker,
    error,
    setError
  } = useLocker();

  const [tenantId, setTenantId] = useState(lockerData?.tenantId || auth.user?.tenant_id || '');
  const mode = id ? 'edit' : 'create';

  // Queries
  const { data: equipments = [], isLoading: loadingEquipments } = useQuery({
    queryKey: ['equipments'],
    queryFn: async () => {
      const res = await equipmentService.list();
      return res.data;
    },
  });

  const { data: manufacturers = [], isLoading: loadingManufacturers } = useQuery({
    queryKey: ['manufacturers'],
    queryFn: async () => {
      const res = await manufacturerService.list();
      return res.data;
    },
  });

  const { data: rooms = [], isLoading: loadingRooms } = useQuery({
    queryKey: ['rooms'],
    queryFn: async () => {
      const res = await roomService.list();
      return res.data;
    },
  });

  const validationSchema = Yup.object().shape({
    name: Yup.string().required('Nome obrigatório'),
    equipmentId: Yup.string().required('Equipamento obrigatório'),
    manufacturerId: Yup.string().required('Fabricante obrigatório'),
    roomId: Yup.string().required('Área obrigatória'),
    locker_quantity: Yup.number().min(1, 'Quantidade deve ser pelo menos 1'),
  });

  const initialValues: LockerFormValues = {
    name: lockerData?.name || '',
    equipmentId: lockerData?.equipmentId || '',
    manufacturerId: lockerData?.manufacturerId || '',
    roomId: lockerData?.roomId || '',
    is_available: lockerData?.is_available ?? true,
    locker_quantity: lockerData?.locker_quantity || 1,
  };

  const handleSubmit = async (values: LockerFormValues) => {
    try {
      const payload: LockerPayload = {
        name: values.name,
        tenantId: auth.selectedTenant.id,
        equipmentId: values.equipmentId,
        manufacturerId: values.manufacturerId,
        roomId: values.roomId,
        is_available: values.is_available,
        number: values.locker_quantity.toString(),
        ...(mode === 'create' && { locker_quantity: values.locker_quantity }),
      };

      if (mode === 'create') {
        await createLocker(payload);
        showToast('Armário criado com sucesso!', 'success');
      } else if (id) {
        const { locker_quantity, ...updatePayload } = payload;
        await updateLocker(id, updatePayload);
        showToast('Armário atualizado com sucesso!', 'success');
      }

      navigate('/lockers');
    } catch (error) {
      console.error('Error submitting form:', error);
    }
  };

  const loadingData = loadingEquipments || loadingManufacturers || loadingRooms || isDataLoading;

  return {
    mode,
    initialValues,
    validationSchema,
    loadingData,
    equipments,
    manufacturers,
    rooms,
    tenantId,
    setTenantId,
    handleSubmit,
    error,
    setError
  };
} 